import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import { Outlet, useLocation } from 'react-router-dom';

import { useColors, useResponsive } from '@inbody/core';
import { useAuth } from '@inbody/keycloak';
import {
  LayoutHeader,
  LayoutSideMenuV2,
  MENU_COLLAPSED_WIDTH,
  MENU_WIDTH,
  MenuItemType,
  useMenuStore,
} from '@inbody/ui';
import { Box, CircularProgress } from '@mui/material';

import { HeaderBreadcrumb } from './HeaderBreadcrumb';
import { ADMIN_MENU_ITEMS, MENU_ITEMS } from './menuItems';

/**
 * IPH 메인 레이아웃.
 *
 * insa-portal 과 같은 구성(LayoutSideMenuV2 + LayoutHeader)을 쓴다.
 * 헤더는 다크모드 전환과 프로필(계정 메뉴)만 두고 나머지(법인 선택·언어·검색)는
 * IPH 에 해당 기능이 없어 끈다.
 */
export const MainLayout = () => {
  const colors = useColors();
  const { isMobile } = useResponsive();
  const { user } = useAuth();
  const location = useLocation();

  const [isMenuCollapsed, setIsMenuCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // 새로고침 시 메뉴 그룹을 모두 닫힌 상태로 시작한다(펼침은 세션 동안만 유지).
  const clearExpandedMenus = useMenuStore((state) => state.clearExpandedMenus);
  useEffect(() => {
    clearExpandedMenus();
  }, [clearExpandedMenus]);

  const handleMobileMenuToggle = useCallback(
    () => setMobileMenuOpen((prev) => !prev),
    [],
  );
  const handleMobileMenuClose = useCallback(() => setMobileMenuOpen(false), []);

  // 모바일에서 화면을 이동하면 메뉴를 닫는다 — 메뉴가 콘텐츠를 덮은 채 남지 않도록.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  /*
    현재 경로가 어느 메뉴인지 찾아 헤더 브레드크럼에 표시한다.

    관리자 메뉴도 함께 훑는다 — 사이드바에서는 구분선으로 갈라져 있지만
    브레드크럼에서는 같은 메뉴 트리다. 빼면 관리자 화면에서만 브레드크럼이
    사라진다.
  */
  const breadcrumbs = useMemo(
    () =>
      findMenuTrail([...MENU_ITEMS, ...ADMIN_MENU_ITEMS], location.pathname),
    [location.pathname],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        backgroundColor: colors.gray[25],
      }}
    >
      <LayoutSideMenuV2
        menuItemsList={MENU_ITEMS}
        adminMenuItemsList={ADMIN_MENU_ITEMS}
        appTitle="IPH"
        appSubtitle="제조그룹 구매 포털"
        headerLayout="horizontal"
        onCollapsedChange={setIsMenuCollapsed}
        mobileOpen={mobileMenuOpen}
        onMobileClose={handleMobileMenuClose}
      />

      <Box
        id="main-content"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          marginLeft: isMobile
            ? 0
            : isMenuCollapsed
              ? `${MENU_COLLAPSED_WIDTH}px`
              : `${MENU_WIDTH}px`,
          transition: 'margin-left 0.3s ease',
          overflow: 'hidden',
        }}
      >
        <LayoutHeader
          // 다크모드 + 프로필만 남긴다.
          showThemeSwitch
          showAccountMenu
          accountMenuShowProfile
          showLanguageChange={false}
          showCorporateChange={false}
          showUserSearch={false}
          userDisplayName={user?.firstName || ''}
          // 공용 breadcrumbText 는 클릭이 되지 않아, 같은 모양의 이동 가능한
          // 브레드크럼을 leftActions 로 직접 넣는다.
          leftActions={<HeaderBreadcrumb crumbs={breadcrumbs} />}
          onMenuToggle={handleMobileMenuToggle}
        />

        {/* 본문 여백도 insa-portal 과 같은 값을 쓴다(TabOutlet 기준). */}
        <Box sx={{ flex: 1, overflow: 'auto', p: { mobile: 1, tablet: 2 } }}>
          <Suspense fallback={<LayoutFallback />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    </Box>
  );
};

const LayoutFallback = () => {
  const { brand } = useColors();

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
      <CircularProgress size={28} sx={{ color: brand[500] }} />
    </Box>
  );
};

/**
 * 브레드크럼 한 조각.
 *
 * `url` 이 있으면 눌러서 이동할 수 있다. 'PO 관리' 같은 그룹은 그 자체로 갈 수
 * 있는 화면이 아니라 url 이 없고, 텍스트로만 남는다.
 */
export interface BreadcrumbCrumb {
  label: string;
  url?: string;
}

/**
 * 현재 경로에 해당하는 메뉴 경로(상위 → 하위)를 찾는다.
 * 못 찾으면 빈 배열이라 브레드크럼이 표시되지 않는다.
 *
 * 가장 **긴 URL이 일치하는 항목**을 고른다 — '/po' 가 '/po/submitted' 의 접두라
 * 단순히 먼저 걸리는 것을 택하면 상신 PO 화면에서 '전체 PO' 가 표시된다.
 */
const findMenuTrail = (
  items: MenuItemType[],
  pathname: string,
): BreadcrumbCrumb[] => {
  let bestTrail: BreadcrumbCrumb[] = [];
  let bestLength = -1;

  const walk = (menuItems: MenuItemType[], parents: BreadcrumbCrumb[]) => {
    for (const item of menuItems) {
      if (item.type === 'link' && item.url && isPathMatch(pathname, item.url)) {
        if (item.url.length > bestLength) {
          bestLength = item.url.length;
          bestTrail = [...parents, { label: item.label, url: item.url }];
        }
      }
      if (item.type === 'expand') {
        // 그룹은 갈 수 있는 화면이 없어 url 을 담지 않는다.
        walk(item.children, [...parents, { label: item.label }]);
      }
    }
  };

  walk(items, []);
  return bestTrail;
};

/** 경로 일치 — 하위 경로는 포함하되 '/poX' 같은 다른 단어와는 섞이지 않게 한다. */
const isPathMatch = (pathname: string, url: string) =>
  pathname === url || pathname.startsWith(`${url}/`);
